// ASANA-FELADAT A JEGYHEZ — a legacy CRM „Asana" gombja, a jegy „Kapcsolódó linkek" szekciójában.
//
// HÁROM ÁLLAPOT, EGY HELYEN:
//   • nincs feladat + van kezelési jog → GOMB (megerősítő dialógussal, mert külső rendszerbe ír);
//   • van feladat → a kattintható hivatkozás, mellette (kezelési joggal) a leválasztás;
//   • nincs feladat és nincs jog → a blokk nem jelenik meg (üres sor nem információ).
//
// MIÉRT KELL MEGERŐSÍTÉS: a gomb egy MÁSIK rendszerben hoz létre valamit, a FELHASZNÁLÓ SAJÁT nevében, és ez
// nem vonható vissza egy „mégse" gombbal (a feladat ott marad, kollégák láthatják). A legacyben nem volt
// megerősítés — egy félrekattintás azonnal taszkot csinált.
//
// MIÉRT NEM TÖRLÜNK AZ ASANÁBAN: a „Leválasztás" CSAK a jegyről veszi le a hivatkozást; a feladat az Asanában
// marad (ott kommentek/csatolmányok/felelősök lehetnek rajta). Egy jegy-oldali gomb ne semmisítsen meg munkát
// egy másik rendszerben — a legacy sem tette. A dialógus szövege ezt KIMONDJA, nem a felhasználóra bízza.
//
// OPTIMISTA UI HELYETT SZERVER-IGAZSÁG: a hivatkozást a szerver állítja elő (az Asana adja vissza), tehát nincs
// mit előre kitalálni — a `useEntityMutation` a választ írja a detail-cache-be, majd invalidál.
import { LinkSlashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Button } from '@/components/ui/button';
import { ticketKeys } from '@/features/tickets/api';
import { api } from '@/lib/api';
import { toHref } from '@/lib/external-url';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export interface TicketAsanaTaskProps {
  ticketId: string;
  /** A jegy verziója — OCC (`If-Match`) mindkét művelethez. */
  version: number;
  /** A jelenlegi hivatkozás, vagy `null`, ha a jegyhez még nem tartozik feladat. */
  asanaTaskUrl: string | null;
  /** Kezelési jog (`tickets.manage`) — enélkül a hivatkozás csak OLVASHATÓ, művelet nincs. */
  canManage: boolean;
}

/**
 * Az ASANA-JEL a gombon és a hivatkozáson (a legacy gombja is ezt viselte — a kezelő erről ismeri fel). MÁRKA-
 * asset (`public/icons/asana-mark.svg`), nem UI-ikon: a Heroicons-szabály a felület ikonjaira szól, egy külső
 * szolgáltatás logója nem helyettesíthető vele. Dekoratív (`alt=""`) — a feliratot a mellette álló szöveg adja.
 */
function AsanaMark(): JSX.Element {
  return <img src="/icons/asana-mark.svg" alt="" aria-hidden className="size-4 shrink-0" />;
}

export function TicketAsanaTask({
  ticketId,
  version,
  asanaTaskUrl,
  canManage,
}: TicketAsanaTaskProps): JSX.Element | null {
  const { t } = useTranslation();
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const detailKey = ticketKeys.detail(ticketId);
  const invalidateKeys = [detailKey, ticketKeys.lists()];
  const messages = {
    success: t('toast:tickets.asanaCreated'),
    conflict: t('toast:tickets.conflict'),
    forbidden: t('toast:tickets.forbidden'),
    validation: t('toast:tickets.validation'),
    error: t('toast:tickets.asanaError'),
  };

  const create = useEntityMutation<void>({
    mutationFn: () =>
      api.ticketCreateAsanaTask.mutation({
        params: { id: ticketId },
        body: {},
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys,
    messages,
    // A MEZŐNKÉNTI hibát (nincs/lejárt személyes token) KI KELL MONDANI: a generikus „ellenőrizd a mezőket"
    // itt hazudna — nincs mit ellenőrizni az űrlapon, a teendő a Profilom oldalon van.
    onFieldErrors: (_field, msgs) => {
      if (msgs[0] !== undefined) toast.error(msgs[0]);
    },
  });

  const unlink = useEntityMutation<void>({
    mutationFn: () =>
      api.ticketUnlinkAsanaTask.mutation({
        params: { id: ticketId },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys,
    messages: { ...messages, success: t('toast:tickets.asanaUnlinked') },
  });

  if (asanaTaskUrl === null || asanaTaskUrl === '') {
    if (!canManage) return null;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setConfirmCreate(true)}
          disabled={create.isPending}
        >
          <AsanaMark />
          {create.isPending ? t('tickets.asana.creating') : t('tickets.asana.create')}
        </Button>
        <ConfirmDialog
          open={confirmCreate}
          onOpenChange={setConfirmCreate}
          title={t('tickets.asana.confirmTitle')}
          description={t('tickets.asana.confirmDescription')}
          confirmLabel={t('tickets.asana.confirm')}
          pendingLabel={t('tickets.asana.creating')}
          cancelLabel={t('tickets.cancel')}
          pending={create.isPending}
          onConfirm={() =>
            create.mutate(undefined, {
              onSuccess: (res) => {
                setConfirmCreate(false);
                // a feladat létrejött, de nem minden melléklet ment át — a teendőt ki kell mondani
                const body = res.body as { attachmentsIncomplete?: boolean } | undefined;
                if (body?.attachmentsIncomplete === true) {
                  toast.warning(t('toast:tickets.asanaAttachmentsIncomplete'));
                }
              },
            })
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <a
        href={toHref(asanaTaskUrl)}
        target="_blank"
        rel="noreferrer"
        className="text-brand-hover flex min-w-0 items-center gap-1.5 truncate text-sm hover:underline"
      >
        <AsanaMark />
        <span className="truncate">{t('tickets.links.asana')}</span>
      </a>
      {canManage && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => setConfirmUnlink(true)}
            disabled={unlink.isPending}
          >
            <LinkSlashIcon className="size-4" />
            {unlink.isPending ? t('tickets.asana.unlinking') : t('tickets.asana.unlink')}
          </Button>
          <ConfirmDialog
            open={confirmUnlink}
            onOpenChange={setConfirmUnlink}
            tone="warning"
            title={t('tickets.asana.unlinkTitle')}
            description={t('tickets.asana.unlinkDescription')}
            confirmLabel={t('tickets.asana.unlink')}
            pendingLabel={t('tickets.asana.unlinking')}
            cancelLabel={t('tickets.cancel')}
            pending={unlink.isPending}
            onConfirm={() => unlink.mutate(undefined, { onSuccess: () => setConfirmUnlink(false) })}
          />
        </>
      )}
    </div>
  );
}
